'use client';

/**
 * Addresses Hook
 *
 * Provides address/location management functionality for customers.
 * Uses the CustomerAuthProvider for API access to Route Royal's
 * Location system via /users/me/profile/contact/addresses.
 */

import { useState, useCallback, useEffect } from 'react';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import type {
  CustomerAddress,
  CreateAddressRequest,
  UpdateAddressRequest,
} from '@/types/customer';

// ============================================================================
// Types
// ============================================================================

export interface UseAddressesReturn {
  /** All addresses for the current customer */
  addresses: CustomerAddress[];
  /** The primary address (convenience getter) */
  primaryAddress: CustomerAddress | null;
  /** Whether addresses are loading */
  isLoading: boolean;
  /** Whether a save operation is in progress */
  isSaving: boolean;
  /** Error if any */
  error: string | null;
  /** Refresh addresses from the API */
  refresh: () => Promise<void>;
  /** Create a new address */
  createAddress: (data: CreateAddressRequest) => Promise<CustomerAddress | null>;
  /** Update an existing address */
  updateAddress: (linkId: string, data: UpdateAddressRequest) => Promise<CustomerAddress | null>;
  /** Set an address as primary */
  setPrimaryAddress: (linkId: string) => Promise<boolean>;
  /** Delete an address */
  deleteAddress: (linkId: string) => Promise<boolean>;
}

// ============================================================================
// Hook
// ============================================================================

export function useAddresses(): UseAddressesReturn {
  const { getCustomerClient, isAuthenticated } = useCustomerAuth();
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Derived: primary address
  const primaryAddress = addresses.find((a) => a.isPrimary) || addresses[0] || null;

  // Fetch addresses
  const refresh = useCallback(async () => {
    const client = getCustomerClient();
    if (!client) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await client.getAddresses();
      // Response may be an array directly or wrapped in ApiResponseDto
      const items = Array.isArray(response) ? response : (response as unknown as { data: CustomerAddress[] })?.data || [];
      setAddresses(items);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load addresses';

      // Handle gracefully: 404, network errors, etc.
      if (
        errorMessage.includes('404') ||
        errorMessage.includes('not found') ||
        errorMessage.includes('Cannot GET') ||
        errorMessage.includes('API server unavailable') ||
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('Network')
      ) {
        setAddresses([]);
      } else {
        console.error('[useAddresses] Failed to fetch addresses:', err);
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [getCustomerClient]);

  // Load addresses when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setAddresses([]);
    }
  }, [isAuthenticated, refresh]);

  // Create a new address
  const createAddress = useCallback(
    async (data: CreateAddressRequest): Promise<CustomerAddress | null> => {
      const client = getCustomerClient();
      if (!client) return null;

      setIsSaving(true);
      setError(null);

      try {
        const address = await client.createAddress(data);
        // If this is set as primary, update others
        if (data.isPrimary) {
          setAddresses((prev) => [
            address,
            ...prev.map((a) => ({ ...a, isPrimary: false })),
          ]);
        } else {
          setAddresses((prev) => [address, ...prev]);
        }
        return address;
      } catch (err) {
        console.error('[useAddresses] Failed to create address:', err);
        setError(err instanceof Error ? err.message : 'Failed to create address');
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [getCustomerClient]
  );

  // Update an existing address
  const updateAddress = useCallback(
    async (linkId: string, data: UpdateAddressRequest): Promise<CustomerAddress | null> => {
      const client = getCustomerClient();
      if (!client) return null;

      setIsSaving(true);
      setError(null);

      try {
        const address = await client.updateAddress(linkId, data);
        setAddresses((prev) => prev.map((a) => (a.id === linkId ? address : a)));
        return address;
      } catch (err) {
        console.error('[useAddresses] Failed to update address:', err);
        setError(err instanceof Error ? err.message : 'Failed to update address');
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [getCustomerClient]
  );

  // Set an address as primary
  const setPrimaryAddress = useCallback(
    async (linkId: string): Promise<boolean> => {
      const client = getCustomerClient();
      if (!client) return false;

      setIsSaving(true);
      setError(null);

      try {
        await client.setPrimaryAddress(linkId);
        // Optimistically update local state
        setAddresses((prev) =>
          prev.map((a) => ({
            ...a,
            isPrimary: a.id === linkId,
          }))
        );
        return true;
      } catch (err) {
        console.error('[useAddresses] Failed to set primary address:', err);
        setError(err instanceof Error ? err.message : 'Failed to set primary address');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [getCustomerClient]
  );

  // Delete an address
  const deleteAddress = useCallback(
    async (linkId: string): Promise<boolean> => {
      const client = getCustomerClient();
      if (!client) return false;

      setIsSaving(true);
      setError(null);

      try {
        await client.deleteAddress(linkId);
        setAddresses((prev) => prev.filter((a) => a.id !== linkId));
        return true;
      } catch (err) {
        console.error('[useAddresses] Failed to delete address:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete address');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [getCustomerClient]
  );

  return {
    addresses,
    primaryAddress,
    isLoading,
    isSaving,
    error,
    refresh,
    createAddress,
    updateAddress,
    setPrimaryAddress,
    deleteAddress,
  };
}

export default useAddresses;
